import React from 'react';
import {
  Upload,
  FileText,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

const STEPS = {
  FILE_UPLOAD: 'file_upload',
  COLUMN_MAPPING: 'column_mapping',
  PREVIEW_IMPORT: 'preview_import'
};

const GenericImporterStepperProgressTracker = ({ activeStep, config, onStepClick }) => {
  const steps = [
    {
      id: 1,
      key: STEPS.FILE_UPLOAD,
      title: 'Ladda upp fil',
      description: 'Välj CSV-fil att importera',
      icon: Upload
    },
    {
      id: 2,
      key: STEPS.COLUMN_MAPPING,
      title: 'Mappa kolumner',
      description: 'Koppla CSV-kolumner till systemfält',
      icon: ArrowRight
    },
    {
      id: 3,
      key: STEPS.PREVIEW_IMPORT,
      title: 'Förhandsgranska & Importera',
      description: 'Kontrollera och slutför import',
      icon: FileText
    }
  ];

  const getCurrentStep = () => {
    if (activeStep === STEPS.FILE_UPLOAD) return 1;
    if (activeStep === STEPS.COLUMN_MAPPING) return 2;
    if (activeStep === STEPS.PREVIEW_IMPORT) return 3;
    return 1;
  };

  const isStepCompleted = (stepId) => {
    return getCurrentStep() > stepId;
  };

  const isStepActive = (stepId) => {
    return getCurrentStep() === stepId;
  };

  const isStepAccessible = (stepId) => {
    const currentStep = getCurrentStep();
    return stepId <= currentStep || isStepCompleted(stepId);
  };

  return (
    <>
      <style>{`
        .progress-tracker {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }
        
        .progress-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        
        .progress-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }
        
        .progress-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }
        
        .steps-container {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          position: relative;
          margin: 2rem 0;
        }
        
        .progress-line {
          position: absolute;
          top: 30px;
          left: 60px;
          right: 60px;
          height: 2px;
          background: #e5e7eb;
          z-index: 0;
        }
        
        .progress-line-fill {
          height: 100%;
          background: #44814E;
          border-radius: 1px;
          transition: width 0.5s ease;
        }
        
        .step-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          position: relative;
          z-index: 1;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 120px;
          max-width: 140px;
        }
        
        .step-item:hover {
          transform: translateY(-2px);
        }
        
        .step-item.disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }
        
        .step-item.disabled:hover {
          transform: none;
        }
        
        .step-circle {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1rem;
          position: relative;
          transition: all 0.3s ease;
          border: 3px solid transparent;
        }
        
        .step-circle.completed {
          background: linear-gradient(135deg, #44814E, #36673e);
          color: white;
          box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3);
        }
        
        .step-circle.active {
          background: linear-gradient(135deg, #44814E, #3a6e42);
          color: white;
          box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3);
          border-color: rgba(68, 129, 78, 0.2);
          animation: pulse 2s infinite;
        }
        
        .step-circle.pending {
          background: #f8fafc;
          color: #9ca3af;
          border: 2px solid #e5e7eb;
        }
        
        .step-circle.accessible.pending:hover {
          background: #f1f5f9;
          border-color: #44814E;
          color: #44814E;
        }
        
        @keyframes pulse {
          0% {
            box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3), 0 0 0 0 rgba(68, 129, 78, 0.4);
          }
          70% {
            box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3), 0 0 0 10px rgba(68, 129, 78, 0);
          }
          100% {
            box-shadow: 0 4px 12px rgba(68, 129, 78, 0.3), 0 0 0 0 rgba(68, 129, 78, 0);
          }
        }
        
        .step-content {
          flex: 1;
        }
        
        .step-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }
        
        .step-description {
          font-size: 0.75rem;
          color: #64748b;
          line-height: 1.4;
        }
        
        .step-item.completed .step-title {
          color: #36673e;
        }
        
        .step-item.active .step-title {
          color: #44814E;
        }
        
        .step-item.completed .step-description {
          color: #3a6e42;
        }
        
        .step-item.active .step-description {
          color: #44814E;
        }
        
        .step-status {
          margin-top: 0.5rem;
          font-size: 0.75rem;
          font-weight: 500;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          display: inline-block;
        }
        
        .status-completed {
          background: rgba(68, 129, 78, 0.15);
          color: #36673e;
        }
        
        .status-active {
          background: rgba(68, 129, 78, 0.25);
          color: #44814E;
        }
        
        .status-pending {
          background: #f1f5f9;
          color: #64748b;
        }
        
        .status-locked {
          background: #fef2f2;
          color: #dc2626;
        }
        
        /* Mobile responsive */
        @media (max-width: 768px) {
          .steps-container {
            flex-direction: column;
            gap: 1rem;
          }
          
          .progress-line {
            display: none;
          }
          
          .step-item {
            flex-direction: row;
            text-align: left;
            min-width: auto;
            max-width: none;
            width: 100%;
            padding: 1rem;
            background: #f8fafc;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
          }
          
          .step-circle {
            width: 50px;
            height: 50px;
            margin-bottom: 0;
            margin-right: 1rem;
            flex-shrink: 0;
          }
          
          .step-content {
            flex: 1;
          }
          
          .step-status {
            margin-top: 0.25rem;
          }
        }
      `}</style>

      <div className="progress-tracker">
        <div className="progress-header">
          <h3 className="progress-title">Importera data - Steg för steg</h3>
          <p className="progress-subtitle">
            Följ dessa steg för att importera {config?.type === 'articles' ? 'artiklar' :
              config?.type === 'customers' ? 'kunder' :
                config?.type === 'suppliers' ? 'leverantörer' : 'data'} till ditt system
          </p>
        </div>

        <div className="steps-container">
          <div className="progress-line">
            <div
              className="progress-line-fill"
              style={{
                width: `${Math.max(0, (getCurrentStep() - 1) / (steps.length - 1) * 100)}%`
              }}
            />
          </div>

          {steps.map((step) => {
            const completed = isStepCompleted(step.id);
            const active = isStepActive(step.id);
            const accessible = isStepAccessible(step.id);
            const Icon = step.icon;

            return (
              <div
                key={step.id}
                className={`step-item ${completed ? 'completed' :
                  active ? 'active' :
                    accessible ? 'accessible' : 'disabled'
                  }`}
                onClick={() => {
                  if (accessible && onStepClick) {
                    onStepClick(step.key);
                  }
                }}
              >
                <div className={`step-circle ${completed ? 'completed' :
                  active ? 'active' : 'pending'
                  } ${accessible ? 'accessible' : ''}`}>
                  {completed ? (
                    <CheckCircle2 size={24} />
                  ) : (
                    <Icon size={20} />
                  )}
                </div>

                <div className="step-content">
                  <div className="step-title">{step.title}</div>
                  <div className="step-description">{step.description}</div>

                  <div className={`step-status ${completed ? 'status-completed' :
                    active ? 'status-active' :
                      accessible ? 'status-pending' : 'status-locked'
                    }`}>
                    {completed ? 'Slutförd' :
                      active ? 'Pågår' :
                        accessible ? 'Tillgänglig' : 'Låst'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default GenericImporterStepperProgressTracker;